import * as React from 'react';
import { cn } from '../../lib/cn';

/**
 * NeonCard — Xeito-branded card primitive for the web.
 *
 * Web-side companion to `apps/mobile/src/components/neon/NeonCard.tsx` —
 * mirrors the same accent / glow semantics but emits Tailwind classes
 * so it composes with the rest of `@xeito/ui`'s shadcn-style primitives.
 *
 * Phase AI-UI-Brand-Migration P3 (2026-06-13). Extracted from the inline
 * `FeatureCard` brand styling in `apps/web/src/routes/$locale/
 * dashboard.ai-features.lazy.tsx`. Adopting this primitive across web
 * dashboard surfaces is the rest of P3.
 *
 * Tailwind JIT note: all class strings live as static template branches
 * (no dynamic `border-${accent}` concatenation), so the JIT scanner picks
 * up every glow/border combination at build time.
 */
export type NeonCardAccent = 'primary' | 'magenta' | 'warning' | 'muted' | 'none';

export interface NeonCardProps extends React.HTMLAttributes<HTMLDivElement> {
    /**
     * Accent colour for the border + (optional) glow. `none` falls through to
     * the neutral shadcn-style card look (same as `<Card>` from `@xeito/ui`).
     */
    accent?: NeonCardAccent;
    /**
     * When true, the card emits an outward glow matching the accent colour.
     * Combine with `hoverGlow` for the standard interactive card pattern
     * (no glow at rest, glow on hover).
     */
    glow?: boolean;
    /**
     * When true, adds a hover-only glow + border colour shift. Mutually
     * compatible with `glow` (glow=true + hoverGlow=true intensifies on hover).
     */
    hoverGlow?: boolean;
}

const ACCENT_BORDER: Record<NeonCardAccent, string> = {
    primary: 'border-primary/40',
    magenta: 'border-magenta/40',
    warning: 'border-warning/40',
    muted: 'border-border bg-muted/30',
    none: 'border-border',
};

const ACCENT_GLOW: Record<NeonCardAccent, string> = {
    primary: 'shadow-[0_0_32px_-8px_var(--color-primary)]',
    magenta: 'shadow-[0_0_32px_-8px_var(--color-magenta)]',
    warning: 'shadow-[0_0_32px_-8px_var(--color-warning)]',
    muted: '',
    none: '',
};

const ACCENT_HOVER: Record<NeonCardAccent, string> = {
    primary: 'hover:border-primary hover:shadow-[0_0_24px_-4px_var(--color-primary)]',
    magenta: 'hover:border-magenta hover:shadow-[0_0_24px_-4px_var(--color-magenta)]',
    warning: 'hover:border-warning hover:shadow-[0_0_24px_-4px_var(--color-warning)]',
    muted: 'hover:border-border',
    none: 'hover:border-border',
};

const NeonCard = React.forwardRef<HTMLDivElement, NeonCardProps>(
    ({ accent = 'none', glow = false, hoverGlow = false, className, ...props }, ref) => (
        <div
            ref={ref}
            data-accent={accent}
            data-glow={glow ? '' : undefined}
            data-hover-glow={hoverGlow ? '' : undefined}
            className={cn(
                'rounded-xl border bg-card text-card-foreground transition-all duration-200',
                ACCENT_BORDER[accent],
                glow ? ACCENT_GLOW[accent] : '',
                hoverGlow ? ACCENT_HOVER[accent] : '',
                className,
            )}
            {...props}
        />
    ),
);
NeonCard.displayName = 'NeonCard';

export { NeonCard };
